import { InfoCard } from '@/components/info-card.js';
import { InfoPage } from '@/components/info-page.js';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import {
  getSchool,
  purgeSchoolDataDryRun,
  purgeSchoolDataEnrolledStudents,
  purgeSchoolDataRequest,
} from '@/lib/api-calls.js';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling.js';
import { validateRouteRole } from '@/lib/routing.js';
import { canPurgeData } from '@/auth/roles.js';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { purgeConfirmationPhrase, PurgeDataRequestResult, PurgeDryRunTable } from '@airreading/common/data';
import { PurgeConfirmationGate } from '../../-components/purge-confirmation-gate.js';
import { PurgeHardDeleteConfirmation } from '../../-components/purge-hard-delete-confirmation.js';
import { PurgeResultSummary } from '../../-components/purge-result-summary.js';
import { PurgeDryRunDownloads } from '../../-components/purge-dry-run-download.js';
import { PurgeEnrolledStudentsGate } from '../../-components/purge-enrolled-students-gate.js';
import { schoolDetailsTabs } from '../-components/tabs.js';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/settings')({
  beforeLoad: validateRouteRole(canPurgeData, { allowAnyAirAdmin: false }),
  component: SchoolSettingsPage,
});

function SchoolSettingsPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load school. Please try again.',
          back: {
            backLink: {
              to: '/orgs/schools',
            },
          },
        };
      }}
    >
      <SchoolSettings />
    </InfoPageSuspenseBoundary>
  );
}

function SchoolSettings() {
  const { schoolId } = Route.useParams();
  const { data } = useSuspenseQuery(getSchool(schoolId));

  return (
    <InfoPage
      headerText={data.name || data.id}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Schools',
            link: {
              to: '/orgs/schools',
            },
          },
        ],
        currentPage: data.name,
      }}
      tabs={schoolDetailsTabs({ schoolId: data.id })}
    >
      <DeleteAllDataCard schoolId={data.id} schoolName={data.name} />
    </InfoPage>
  );
}

function DeleteAllDataCard({ schoolId, schoolName }: { schoolId: string; schoolName: string }) {
  const { toast } = useToast();

  const [confirmedName, setConfirmedName] = useState<string | null>(null);
  const [exportsSettled, setExportsSettled] = useState(false);
  const [failedTables, setFailedTables] = useState<PurgeDryRunTable[]>([]);
  const [purgeResult, setPurgeResult] = useState<PurgeDataRequestResult | null>(null);

  const onConfirmed = useCallback(
    (name: string) => {
      setConfirmedName(name);
      toast({ title: `Checking student statuses for ${schoolName}` });
    },
    [schoolName, toast],
  );

  const runDryRun = useCallback(
    (table: PurgeDryRunTable, part: number) =>
      purgeSchoolDataDryRun({ schoolId, table, part, data: { schoolName: confirmedName ?? schoolName } }),
    [schoolId, confirmedName, schoolName],
  );

  const onEnrolledStudentsCleared = useCallback(
    () => toast({ title: `Data purge dry run started for ${schoolName}` }),
    [schoolName, toast],
  );

  const checkEnrolledStudents = useCallback(
    () => purgeSchoolDataEnrolledStudents({ schoolId, data: { schoolName: confirmedName ?? schoolName } }),
    [schoolId, confirmedName, schoolName],
  );

  const onExportsSettled = useCallback((failed: PurgeDryRunTable[]) => {
    setExportsSettled(true);
    setFailedTables(failed);
  }, []);

  const onPurgeConfirmed = useCallback(() => {
    purgeSchoolDataRequest({ schoolId, data: { schoolName: confirmedName ?? schoolName } })
      .then((result) => {
        setPurgeResult(result);
        toast({ title: result.message, variant: result.status === 'error' ? 'destructive' : undefined });
      })
      .catch((e: unknown) =>
        toast({
          title: airServerErrorFrom(e)?.userFacingMessage || 'Failed to request the data purge. Please try again.',
          variant: 'destructive',
        }),
      );
  }, [schoolId, confirmedName, schoolName, toast]);

  return (
    <InfoCard headerText="Delete all data" maxItemsPerRow={1}>
      <PurgeConfirmationGate
        expectedName={schoolName}
        label="Confirm School Name"
        action="Confirm"
        note="Before deleting data we will generate CSV exports of the data that would be deleted. Proceed to generate exports. After validating exports we will ask you to confirm again before deleting data."
        testId="school-settings-delete-all-data"
        onConfirmed={onConfirmed}
      />
      {confirmedName !== null && (
        <PurgeEnrolledStudentsGate checkEnrolledStudents={checkEnrolledStudents} onCleared={onEnrolledStudentsCleared}>
          <PurgeDryRunDownloads runDryRun={runDryRun} onExportsSettled={onExportsSettled} />
          {exportsSettled && purgeResult === null && (
            <PurgeHardDeleteConfirmation
              expectedName={schoolName}
              expectedInput={purgeConfirmationPhrase(schoolName)}
              label="Confirm Permanent Delete"
              action="Delete all data"
              checkboxLabel="I have reviewed the dry run data and want to continue with the hard delete of all school data"
              disabled={failedTables.length > 0}
              testId="school-settings-purge-data-request"
              onConfirmed={onPurgeConfirmed}
            />
          )}
          {purgeResult !== null && <PurgeResultSummary result={purgeResult} />}
        </PurgeEnrolledStudentsGate>
      )}
    </InfoCard>
  );
}
